const DEFAULT_PLUVIOMETRIC_CHART_ADVANCED_CONFIGURATION = {
  fieldArea: {},
  cannonRadius: {},
  flowPerArea: {},
  flowPerDay: {},
  cannonFlow: {},
  fullApplicationRate: {},
  pressure: { lowestElevation: {}, highestElevation: {}, basePress: {}, pivotPress: {}, endPress: {} },
  pipes: { tubes: [] },
  flowPerSpan: { spans: [] },
  lrduDriveTrain: {},
  sprinklerConfiguration: { sprinklersPerSpan: [] }
}

/** Default field source: 'auto' = from webhook automatic reading, 'manual' = user input */
const DEFAULT_FIELD_SOURCE = {
  pumpFlow: 'manual',
  brand: 'manual',
  model: 'manual',
  deviceLength: 'manual',
  area: 'manual',
  equipmentReach: 'manual',
  hoseLength: 'manual',
  chartPressure: 'manual',
  minPressure: 'manual',
  maxPressure: 'manual'
}

const DEFAULT_PLUVIOMETRIC_CHART_DATA = {
  deviceLength: { unit: 'm' },
  pumpFlow: { unit: 'm3/h' },
  area: { unit: 'ha' },
  pressureRange: { unit: 'bar' },
  advancedConfiguration: DEFAULT_PLUVIOMETRIC_CHART_ADVANCED_CONFIGURATION,
  equipmentReach: { unit: 'm' },
  hoseLength: { unit: 'm' },
  fieldSource: { ...DEFAULT_FIELD_SOURCE }
}

// Assign to module.exports properties to avoid "read only property 'exports'" when used with ESM imports
const allExports = {
  EQUIPMENT_TYPE: {
    PIVOT: 1,
    AF: 2,
    IR: 3
  },
  EQUIPMENT_SUBTYPE: {
    AF: {
      TWO_WINGS: 1,
      ONE_WING: 2,
      PIVOTABLE: 3
    }
  },
  EQUIPMENT_TYPES_LABELS: {
    1: 'pivot',
    2: 'forwardAvance',
    3: 'irrigationReel'
  },
  defaultRoles: {
    farmhand: false,
    fieldman: false,
    manager: false,
    owner: false
  },
  USER_ROLES: {
    ADMIN: 'admin',
    FARMHAND: 'farmhand',
    FIELDMAN: 'fieldman',
    MANAGER: 'manager',
    OWNER: 'owner',
    AUDITS: 'audits'
  },
  PROFILE_PIC_FILE_SIZE_LIMIT: 4,
  ALLOWED_IMAGES_FILE_TYPES: [
    'image/png',
    'image/jpg',
    'image/jpeg'
  ],
  ALLOWED_IMAGES_FILE_EXTENSIONS: '.png, .jpg, .jpeg',
  NOTIFICATIONS_TYPES: ['failure', 'off', 'position', 'pressure', 'irrigationOrders'],
  ALLOWED_PLUVIOMETRIC_CHARTS_FILE_TYPES: [
    'image/png',
    'image/tiff',
    'image/jpg',
    'image/jpeg',
    'application/pdf'
  ],
  ALLOWED_PLUVIOMETRIC_CHARTS_FILE_EXTENSIONS: '.pdf,.png,.jpg,.jpeg,.tiff,.tif',
  WEBHOOKS_PATHS: {
    PHONE_EXISTS: '/utilities/phoneExists',
    CREATE_USER: '/utilities/createUser',
    VALIDATE_USER_TOKEN: '/utilities/validateUserToken',
    UPDATE_CONTACT_DATA: '/mobileApp/updateHubspotContact',
    LINK_USERS: '/mobileApp/linkUsersToCampaign',
    LOAD_CAMPAIGN_DATA: '/mobileApp/loadCampaignData',
    SERVICE_EXTENSION: '/mobileApp/serviceExtension',
    LINK_SHORTENER: '/utilities/linkShortener',
    PROCESS_PLUVIOMETRIC_CHART: '/pluviometricCharts/process'
  },
  HTTP_STATUS: {
    OK: 200
  },
  CUSTOM_ERROR_CODES: {
    TOKEN_EXPIRED: 'TOKEN_EXPIRED'
  },
  MIDDLEWARE_PATHS: {
    V1: '/v1',
    ALTIMETRY: '/altimetry'
  },
  SATISFACTION_SURVEY: {
    QUESTION_TYPE: {
      RATING: 'rating',
      OPTIONS: 'options',
      TEXT: 'text'
    },
    OPTIONS: {
      OTHER: 'other',
      MISSING_DATA: 'missingData',
      INACCURATE_DATA: 'inaccurateData',
      CONFUSING_INFO: 'confusingInfo'
    },
    FUNCTIONALITIES: {
      REPORTS: 'reports'
    },
    DEFAULT_COUNT: 3,
    DEFAULT_COOLDOWN: 30,
    DEFAULT_COOLDOWN_UNIT: 'days'
  },
  PRESSURE: {
    MIN: '0.1',
    MIN_RANGE: 0.8,
    MAX_RANGE: 0.8
  },
  DEFAULT_START_ANGLE: 0,
  EVENTS_TO_TRACK: {
    OPEN_APP: 'Open app',
    CLICK_OPEN_MENU_BUTTON: 'Click open menu button',
    CLICK_CLOSE_MENU_BUTTON: 'Click close menu button',
    CLICK_ORGANIZATION_HOME_VIEW_BUTTON: 'Click organization home view button',
    CLICK_REPORTS_VIEW_BUTTON: 'Click reports view button',
    CLICK_REPORTS_DATE_SELECTOR: 'Click reports date selector',
    OPEN_FULL_REPORT: 'Open full report',
    EXPAND_DEVICE_REPORT_PANEL: 'Expand device report panel',
    COLLAPSE_DEVICE_REPORT_PANEL: 'Collapse device report panel',
    CLICK_SELECT_LOT_REPORT: 'Click select lot report',
    CLICK_UNSELECT_LOT_REPORT: 'Click unselect lot report',
    CLICK_SELECT_CIRCULAR_MAP_LAYER_OPTION: 'Click select circular map layer option',
    CLICK_SELECT_CIRCULAR_MAP_LAYER_SUBOPTION: 'Click select circular map layer suboption',
    CLICK_UNSELECT_CIRCULAR_MAP_LAYER_SUBOPTION: 'Click unselect circular map layer suboption',
    DOWNLOAD_PLUVIOMETRIC_CHART: 'Click download pluviometric chart',
    CLICK_OPEN_DEVICE_DETAILS_DIALOG: 'Click open device details dialog',
    CLICK_ORGANIZATION_HOME_DEVICE_MENU: 'Click organization home device menu',
    CLICK_ORGANIZATION_HOME_FIELD_MENU: 'Click organization home field menu',
    CLICK_ADD_DEVICE_BUTTON: 'Click add device button',
    CLICK_ADD_FIELD_BUTTON: 'Click add field button',
    CLICK_ADD_USER_BUTTON: 'Click add user button',
    CLICK_OPEN_FIELD_BUTTON: 'Click open field button',
    CLICK_GO_TO_ORGANIZATION_HOME_FROM_FIELD_BUTTON: 'Click go to organization home from field button',
    CLICK_DELETE_USER_BUTTON: 'Click delete user button',
    CLICK_CONFIRM_DELETE_USER_BUTTON: 'Click confirm delete user button',
    CLICK_SAVE_USER_BUTTON: 'Click save user button',
    CLICK_OPEN_PERMISSIONS_BY_DEVICE_BUTTON: 'Click open permissions by device button',
    REQUEST_AUDIT_QUOTE: 'Click request audit quote button',
    CLICK_EDIT_CAMPAIGN_BUTTON: 'Click edit campaign button',
    CLICK_START_CAMPAIGN_BUTTON: 'Click start campaign button',
    CLICK_CAMPAIGN_TERMS_AND_CONDITIONS_CHECKBOX: 'Click campaign terms and conditions checkbox',
    CLICK_AGREE_TERMS_AND_CONDITIONS_BUTTON: 'Click agree terms and conditions button',
    CLICK_CAMPAIGN_ONBOARDING_INVITE_USERS_BUTTON: 'Click campaign onboarding invite users button',
    CLICK_CAMPAIGN_ONBOARDING_ADD_NEW_DEVICE_BUTTON: 'Click campaign onboarding add new device button',
    CLICK_CAMPAIGN_ONBOARDING_ADD_NEW_FIELD_BUTTON: 'Click campaign onboarding add new field button',
    CLICK_CAMPAIGN_ONBOARDING_BACK_BUTTON: 'Click campaign onboarding back button',
    CLICK_CAMPAIGN_ONBOARDING_CREATE_NEW_DEVICE_BUTTON: 'Click campaign onboarding create new device button',
    CLICK_CAMPAIGN_ONBOARDING_DEVICES_AND_SERVICES_FINISH_BUTTON: 'Click campaign onboarding devices and services finish button',
    CLICK_CAMPAIGN_ONBOARDING_FINISH_BUTTON: 'Click campaign onboarding finish button',
    CLICK_CREATE_NEW_USER_BUTTON: 'Click create new user button',
    CLICK_INVITE_EXISTING_USERS_BUTTON: 'Click invite existing users button',
    CLICK_EDIT_CAMPAIGN_BACK_BUTTON: 'Click edit campaign back button',
    CLICK_EDIT_CAMPAIGN_SAVE_CHANGES_BUTTON: 'Click edit campaign save changes button',
    CLICK_EDIT_CAMPAIGN_ADD_NEW_FIELD_BUTTON: 'Click edit campaign add new field button',
    CLICK_ADD_NEW_FIELD_CONTACT_USER_BUTTON: 'Click add new field contact user button',
    CLICK_PENDING_CAMPAIGN_ONBOARDING_REMIND_ME_LATER_BUTTON: 'Click pending campaign onboarding remind me later button',
    CLICK_PENDING_CAMPAIGN_ONBOARDING_START_BUTTON: 'Click pending campaign onboarding start button',
    CLICK_START_DATE_WARNING_PRELOAD_BUTTON: 'Click start date warning preload button',
    CLICK_ACCEPT_SERVICE_EXTENSION: 'Click accept service extension',
    CLOSE_SERVICE_EXTENSION_DIALOG: 'Close service extension dialog',
    REQUEST_SERVICE_EXTENSION: 'Request service extension',
    CLICK_REQUEST_SERVICE_EXTENSION_BUTTON: 'Click request service extension button',
    CLICK_CAMPAIGN_ONBOARDING_ADD_NEW_EXTENSION_BUTTON: 'Click campaign onboarding add new extension button',
    CLICK_CAMPAIGN_ONBOARDING_REMOVE_EXTENSION_BUTTON: 'Click campaign onboarding remove extension button',
    CLICK_AUDITS_VIEW_BUTTON: 'Click audits view button',
    EXPAND_DEVICE_AUDIT_PANEL: 'Expand device audit panel',
    COLLAPSE_DEVICE_AUDIT_PANEL: 'Collapse device audit panel',
    OPEN_FULL_AUDIT_DEAL: 'Open full audit deal',
    FIELD_SURVEY_AUTO_REDIRECT_FROM_AUDITS: 'Field survey auto redirect from audits',
    FIELD_SURVEY_PENDING_DIALOG_SHOWN: 'Field survey pending dialog shown',
    FIELD_SURVEY_PENDING_COMPLETE_LATER: 'Field survey pending complete later',
    FIELD_SURVEY_PENDING_GO_TO_SETUP: 'Field survey pending go to setup',
    FIELD_SURVEY_CTA_GO_TO_SETUP: 'Field survey CTA go to setup',
    FIELD_SURVEY_FORM_LOADED: 'Field survey form loaded',
    FIELD_SURVEY_FORM_ACCESS_DENIED: 'Field survey form access denied',
    FIELD_SURVEY_FORM_NOT_FOUND: 'Field survey form not found',
    FIELD_SURVEY_SURVEY_WORK_SAVED: 'Field survey survey work saved',
    FIELD_SURVEY_SURVEY_WORK_CREATED_FROM_MAP: 'Field survey survey work created from map',
    FIELD_SURVEY_SURVEY_WORK_DELETED: 'Field survey survey work deleted',
    FIELD_SURVEY_MAP_ADD_BLOCKED: 'Field survey map add blocked',
    FIELD_SURVEY_COMPLETED: 'Field survey completed',
    FIELD_SURVEY_PONCE_CHARGE_DIALOG_OPENED: 'Field survey Ponce charge dialog opened',
    FIELD_SURVEY_TECHNICIAN_USER_CREATED: 'Field survey technician user created'
  },
  DEFAULT_PERMISSIONS_BY_ROLE: {
    admin: [],
    owner: ['failure', 'off'],
    manager: ['failure', 'off'],
    fieldman: ['failure', 'off', 'pressure'],
    farmhand: ['failure', 'off', 'irrigationOrders', 'position', 'pressure']
  },
  DEFAULT_DAYS_BEFORE_START_TO_ALLOW_INITIAL_DATA: 90,
  CAMPAIGN_STEPS: {
    INTRODUCTION: 'introduction',
    T_AND_C: 't&c',
    EQUIPMENTS: 'equipments',
    FINISH: 'finish'
  },
  FIRESTORE_CAMPAIGN_PRE_DATA_STEPS: {
    T_AND_C: 'T&C',
    INTRODUCTION: 'Introduction',
    DEVICES: 'Devices',
    FINISH: 'Finish'
  },
  HUBSPOT_ONBOARDING_STATES: {
    NEW: 'NEW',
    FIRST_ACCESS: 'FIRST_ACCESS',
    ACCEPTED_TYC: 'ACCEPTED_TYC',
    CAMPAIGN_CREATED: 'CAMPAIGN_CREATED',
    COMPLETED_ONBOARDING: 'COMPLETED_ONBOARDING'
  },
  CAMPAIGN_VALIDATION: {
    MIN_DAYS_BEFORE_START: 21
  },
  DEVICE_CONSTANTS: {
    DAYS_FOR_END_OF_SERVICE_NOTICE: 10
  },
  PRODUCT_TYPES: {
    MONITORING: 'monitoring',
    EXTENSION: 'extension'
  },
  AUDITS: {
    OTHER_MOTOR_RPM_ID: '3',
    NO_VISIBLE_MOTOR_RPM_ID: '0',
    ALTIMETRY: {
      DISTANCE_TO_CENTER_DIFFERENCE: 30,
      ANGLES_DIFFERENCE: 30
    },
    OUTLET_TYPES: {
      PLUG: {
        ID: '0',
        LABEL: 'Tapón'
      },
      OUTLET: {
        ID: '1',
        LABEL: 'Bajante'
      },
      END_OF_PIVOT_SPRINKLER: {
        ID: '2',
        LABEL: 'Aspersor de fin de pivot'
      },
      CANNON: {
        ID: '3',
        LABEL: 'Cañón'
      },
      SANDTRAP: {
        ID: '4',
        LABEL: 'Trampa de arena'
      }
    },
    SPAN_TYPE_SPAN_ID: '0',
    SPRINKLER_TYPES: {
      PLUG: '0',
      WITHOUT_SPRINKLER: '1'
    },
    NOZZLE: {
      NULL: '0'
    }
  },
  DEFAULT_PLUVIOMETRIC_CHART_DATA,
  DEFAULT_FIELD_SOURCE,
  DEFAULT_PLUVIOMETRIC_CHART_ADVANCED_CONFIGURATION
}

const {
  EQUIPMENT_TYPE,
  EQUIPMENT_SUBTYPE,
  EQUIPMENT_TYPES_LABELS,
  defaultRoles,
  USER_ROLES,
  PROFILE_PIC_FILE_SIZE_LIMIT,
  ALLOWED_IMAGES_FILE_TYPES,
  ALLOWED_IMAGES_FILE_EXTENSIONS,
  NOTIFICATIONS_TYPES,
  ALLOWED_PLUVIOMETRIC_CHARTS_FILE_TYPES,
  ALLOWED_PLUVIOMETRIC_CHARTS_FILE_EXTENSIONS,
  WEBHOOKS_PATHS,
  HTTP_STATUS,
  CUSTOM_ERROR_CODES,
  MIDDLEWARE_PATHS,
  SATISFACTION_SURVEY,
  PRESSURE,
  DEFAULT_START_ANGLE,
  EVENTS_TO_TRACK,
  DEFAULT_PERMISSIONS_BY_ROLE,
  DEFAULT_DAYS_BEFORE_START_TO_ALLOW_INITIAL_DATA,
  CAMPAIGN_STEPS,
  FIRESTORE_CAMPAIGN_PRE_DATA_STEPS,
  HUBSPOT_ONBOARDING_STATES,
  CAMPAIGN_VALIDATION,
  DEVICE_CONSTANTS,
  PRODUCT_TYPES,
  AUDITS
} = allExports

export {
  EQUIPMENT_TYPE,
  EQUIPMENT_SUBTYPE,
  EQUIPMENT_TYPES_LABELS,
  defaultRoles,
  USER_ROLES,
  PROFILE_PIC_FILE_SIZE_LIMIT,
  ALLOWED_IMAGES_FILE_TYPES,
  ALLOWED_IMAGES_FILE_EXTENSIONS,
  NOTIFICATIONS_TYPES,
  ALLOWED_PLUVIOMETRIC_CHARTS_FILE_TYPES,
  ALLOWED_PLUVIOMETRIC_CHARTS_FILE_EXTENSIONS,
  WEBHOOKS_PATHS,
  HTTP_STATUS,
  CUSTOM_ERROR_CODES,
  MIDDLEWARE_PATHS,
  SATISFACTION_SURVEY,
  PRESSURE,
  DEFAULT_START_ANGLE,
  EVENTS_TO_TRACK,
  DEFAULT_PERMISSIONS_BY_ROLE,
  DEFAULT_DAYS_BEFORE_START_TO_ALLOW_INITIAL_DATA,
  CAMPAIGN_STEPS,
  FIRESTORE_CAMPAIGN_PRE_DATA_STEPS,
  HUBSPOT_ONBOARDING_STATES,
  CAMPAIGN_VALIDATION,
  DEVICE_CONSTANTS,
  PRODUCT_TYPES,
  AUDITS,
  DEFAULT_PLUVIOMETRIC_CHART_DATA,
  DEFAULT_FIELD_SOURCE,
  DEFAULT_PLUVIOMETRIC_CHART_ADVANCED_CONFIGURATION
}
export default allExports


// WEBPACK FOOTER //
// ./src/constants/const.js